import type { AbortLike, HttpClient, HttpRequest, HttpResponse } from '@visious/core';

/**
 * The web half of R2's transport seam. Core never calls `fetch`; this is the only place in the
 * app that does for API traffic. (Shaka has its own networking stack — see `player/shakaPlayer.ts`.)
 */

/**
 * Core's `AbortLike` is a structural subset of `AbortSignal` (R1: no DOM types in core). A real
 * `AbortSignal` satisfies it, so the common case is a pass-through; anything else gets bridged.
 */
function toAbortSignal(signal: AbortLike | undefined): AbortSignal | undefined {
  if (!signal) return undefined;
  if (signal instanceof AbortSignal) return signal;

  const controller = new AbortController();
  if (signal.aborted) controller.abort();
  else signal.addEventListener('abort', () => controller.abort());
  return controller.signal;
}

function readHeaders(headers: Headers): Record<string, string> {
  const out: Record<string, string> = {};
  headers.forEach((value, key) => {
    out[key.toLowerCase()] = value;
  });
  return out;
}

/**
 * An **empty header value means omit the header** — the convention `curl -H 'Header: '` uses, and it
 * is a guard rather than a nicety: measured in 0g, an `Authorization:` with no value reaches the
 * instance as a **malformed token** — `{"error":"unexpected token '<EOF>' at line 1, column 1"}`, the
 * Crystal JSON parser again — and not as an anonymous request. So a caller that has an empty
 * credential in hand sends none at all.
 */
function headersToSend(headers: Readonly<Record<string, string>>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(headers)) {
    if (value.trim() !== '') out[key] = value;
  }
  return out;
}

export function createFetchHttpClient(): HttpClient {
  return {
    async send(request: HttpRequest): Promise<HttpResponse> {
      const response = await fetch(request.url, {
        method: request.method,
        headers: headersToSend(request.headers),
        body: request.body,
        signal: toAbortSignal(request.signal),
        // §3 — the instance is cross-origin and auth is a bearer token, never a cookie.
        // Sending credentials would also force the instance to echo an exact origin.
        credentials: 'omit',
        redirect: 'follow',
        cache: 'no-store',
      });

      return {
        status: response.status,
        headers: readHeaders(response.headers),
        body: await response.text(),
        url: response.url,
      };
    },
  };
}
